import React, { ChangeEvent, useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { isImeComposing } from 'reusable/isImeComposing';

export interface MultiSelectOption {
    name: string;
    id: string;
}

interface MultiSelectProps {
    /** 開閉のボタンの id。項目名の label の htmlFor に使う。 */
    id: string;
    /** 項目名（開閉のボタンと候補の組の名前）。 */
    label: string;
    /** 候補のチェックボックスの name。 */
    name: string;
    options?: MultiSelectOption[];
    selected: { name: string; id: string | number }[];
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    onRemove: (option: { name: string; id: string | number }) => void;
    readOnly?: boolean;
    /** PR #253 の Codex の P2（4257906741）。欄の説明（注記）の id。開閉のボタンの aria-describedby に結ぶ。 */
    describedBy?: string;
    /** 何も選んでいないときに開閉のボタンに出す案内。 */
    placeholder?: React.ReactNode;
    boxClassName?: string;
}

/**
 * ADM-16-AC2・EV-197。共通の追加（AddNew）・編集（Edit）の複数選択。
 *
 * 以前は開閉の要素（role="button" の div）の中に、選んだ項目を外すボタンを入れていた（ボタンの中の
 * ボタン。ARIA では role="button" の子は表示用として扱われ、支援技術から中のボタンが読めない・押せない
 * ことがある）。開閉のボタン（aria-expanded・aria-controls）と外すボタンを兄弟に分ける。
 *
 * - 候補を開いて Escape: 候補だけを閉じて開閉のボタンへ戻る（ダイアログは閉じない。preventDefault
 *   するので useModalDialog の Escape は動かない）。閉じた状態の Escape はダイアログを閉じる。
 * - 外すボタンを押したら、次の外すボタン（無ければ前、それも無ければ開閉のボタン）へフォーカスを移す。
 * - 外（この項目の外）を押したら候補を閉じる。
 */
const MultiSelect: React.FC<MultiSelectProps> = ({
    id,
    label,
    name,
    options,
    selected,
    open,
    onOpenChange,
    onChange,
    onRemove,
    readOnly,
    describedBy,
    placeholder,
    boxClassName = '',
}) => {
    const { t } = useTranslation('common');
    const panelId = useId();
    const rootRef = useRef<HTMLDivElement | null>(null);
    const toggleRef = useRef<HTMLButtonElement | null>(null);
    // 外した項目の位置。描画の後に、その位置の外すボタン（無ければ開閉のボタン）へフォーカスを移す。
    const pendingFocus = useRef<number | null>(null);
    const expanded = open && !readOnly;

    useEffect(() => {
        if (!expanded) return undefined;
        const onMouseDown = (event: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(event.target as Node)) onOpenChange(false);
        };
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [expanded, onOpenChange]);

    useEffect(() => {
        const index = pendingFocus.current;
        if (index === null) return;
        pendingFocus.current = null;
        const buttons = Array.from(
            rootRef.current?.querySelectorAll<HTMLButtonElement>('[data-remove-option]') ?? []
        );
        const next = buttons[Math.min(index, buttons.length - 1)];
        (next ?? toggleRef.current)?.focus();
    }, [selected]);

    const toggle = () => {
        if (!readOnly) onOpenChange(!open);
    };

    const remove = (index: number, option: { name: string; id: string | number }) => {
        pendingFocus.current = index;
        onRemove(option);
    };

    const onKeyDown = (event: React.KeyboardEvent) => {
        if (event.key !== 'Escape' || !expanded || event.defaultPrevented) return;
        if (isImeComposing(event)) return; // EV-120
        event.preventDefault();
        onOpenChange(false);
        toggleRef.current?.focus();
    };

    const isChecked = (option: MultiSelectOption) =>
        selected.some((item) => item.id === option.id);

    return (
        <div className="rounded-md w-full relative" ref={rootRef} onKeyDown={onKeyDown}>
            {/* 枠の余白・選んだ項目の名前を押しても開閉する（同じ操作のボタンが中にある）。 */}
            <div
                className={`border p-2 rounded-md flex flex-wrap items-center cursor-pointer ${boxClassName}`}
                data-pointer-shortcut
                onClick={(event) => {
                    if (!(event.target as HTMLElement).closest('button')) toggle();
                }}
            >
                {selected.length > 0 && (
                    <ul className="flex flex-wrap items-center" aria-label={t('ui.selected-options')}>
                        {selected.map((option, index) => (
                            <li key={`${option.id}-${index}`} className="inline-block bg-gray-200 rounded px-2 mr-2 mb-0">
                                {/* EV-338。候補（機器）の名前が空なら「-」（EV-311。空の項目にしない）。 */}
                                {option.name || '-'}
                                {!readOnly && (
                                    <button
                                        type="button"
                                        className="ml-1 text-red-500"
                                        data-remove-option
                                        aria-label={`${option.name || '-'} ${t('ui.remove-option')}`}
                                        onClick={() => remove(index, option)}
                                    >
                                        <span aria-hidden="true">&times;</span>
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
                <button
                    type="button"
                    id={id}
                    ref={toggleRef}
                    className="flex-1 min-w-[2rem] min-h-[1.5rem] text-left cursor-pointer"
                    aria-label={label}
                    aria-expanded={expanded}
                    aria-controls={panelId}
                    aria-disabled={readOnly || undefined}
                    aria-describedby={describedBy}
                    onClick={toggle}
                >
                    {selected.length === 0 ? placeholder : null}
                </button>
            </div>
            {expanded && (
                <div
                    id={panelId}
                    role="group"
                    aria-label={label}
                    className="absolute mt-2 p-2 bg-white shadow-md w-full z-10 max-h-48 overflow-y-auto"
                >
                    {options?.map((option, index) => (
                        <div key={index} className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                id={`${id}-${index}`}
                                name={name}
                                value={JSON.stringify(option)}
                                checked={isChecked(option)}
                                onChange={onChange}
                            />
                            <label htmlFor={`${id}-${index}`}>{option.name || '-'}</label>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MultiSelect;
